import { useQuery } from '@tanstack/react-query';
import {
  CohortDefaultIndividualServices,
  CohortDescriptions,
  CohortLetters,
  type CohortDto,
  type CohortLetter,
  type IndividualServiceType,
} from '@census/shared';
import { api } from './api';

/**
 * Single TanStack hook the UI uses to read the cohort master list. We GET
 * /cohorts on first call and cache for 5 minutes — admin edits in the Master
 * Library page invalidate this key directly so updates land immediately.
 *
 * If the network is dead or the API hasn't been bootstrapped yet, we surface
 * a legacy fallback list built from the shared enums. That keeps the intake
 * wizard usable on the first launch before the seed has run.
 */
export function useCohorts(opts?: { activeOnly?: boolean }) {
  const q = useQuery({
    queryKey: ['cohorts', { activeOnly: !!opts?.activeOnly }],
    queryFn: () =>
      api
        .get<CohortDto[]>('/cohorts', { params: opts?.activeOnly ? { activeOnly: 'true' } : {} })
        .then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const cohorts = q.data ?? legacyFallback();
  const byCode = new Map<string, CohortDto>(cohorts.map((c) => [c.code, c]));
  return { cohorts, byCode, isLoading: q.isLoading, isError: q.isError };
}

/** Build a DTO-shaped fallback array from the shared enum so the UI doesn't
 *  go blank when the master list hasn't loaded yet. Marked active=true so
 *  pickers default to showing every legacy letter. */
function legacyFallback(): CohortDto[] {
  const now = new Date().toISOString();
  return CohortLetters.map((letter, i) => ({
    id: `legacy-${letter}`,
    code: letter,
    name: CohortDescriptions[letter] ?? letter,
    description: null,
    requiredGroups: [],
    defaultIndividualServices: [
      ...((CohortDefaultIndividualServices[letter] ?? []) as IndividualServiceType[]),
    ],
    sortOrder: 10 + i * 10,
    active: true,
    createdAt: now,
    updatedAt: now,
  }));
}

/**
 * Synchronous helpers for the call sites that already have a cohort code in
 * hand and only need its label. These prefer the cached query (set via
 * useCohorts upstream) and fall back to the enum.
 */
export function cohortNameOrCode(cohort: string, byCode: Map<string, CohortDto>): string {
  const row = byCode.get(cohort);
  if (row?.name) return row.name;
  return (CohortDescriptions as any)[cohort as CohortLetter] ?? cohort;
}
